/**
 * @file Racine de l'UI : couches (HUD, ecrans, modales, toasts, tutoriel), routeur d'ecrans, cadence de mise a jour, re-rendu au changement de langue.
 */
import type { Component } from './core/Component';
import { h } from './core/dom';
import { Hud } from './hud/Hud';
import { motionReduced } from './core/motion';
import { ConfirmDialog } from './modals/ConfirmDialog';
import { OfflineModal } from './modals/OfflineModal';
import { ResultBanner } from './modals/ResultBanner';
import { Toasts } from './modals/Toasts';
import { TutorialOverlay } from './modals/TutorialOverlay';
import { HubShopScreen } from './screens/HubShopScreen';
import { MissionsScreen } from './screens/MissionsScreen';
import { ModesScreen } from './screens/ModesScreen';
import { OnlineScreen } from './screens/OnlineScreen';
import { RebirthScreen } from './screens/RebirthScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { SkinsScreen } from './screens/SkinsScreen';
import { WeaponsScreen } from './screens/WeaponsScreen';
import type { ScreenId, UiContext } from './UiContext';

const SCREENS: Record<ScreenId, (ctx: UiContext) => Component> = {
  weapons: (c) => new WeaponsScreen(c),
  skins: (c) => new SkinsScreen(c),
  hubshop: (c) => new HubShopScreen(c),
  rebirth: (c) => new RebirthScreen(c),
  missions: (c) => new MissionsScreen(c),
  modes: (c) => new ModesScreen(c),
  settings: (c) => new SettingsScreen(c),
  online: (c) => new OnlineScreen(c),
};

/** Cadences : HUD ~30 Hz, ecran ouvert ~10 Hz (les valeurs idle n'ont pas besoin de 60 Hz). */
const HUD_PERIOD = 1 / 30;
const SCREEN_PERIOD = 1 / 10;

export class UiApp {
  readonly root: HTMLElement;
  private readonly layers: Record<'hud' | 'screen' | 'modal' | 'toast' | 'overlay', HTMLElement>;
  private hud: Hud | null = null;
  private screen: { id: ScreenId; comp: Component } | null = null;
  private offline: OfflineModal | null = null;
  private toasts: Toasts | null = null;
  private banner: ResultBanner | null = null;
  private tutorial: TutorialOverlay | null = null;
  private hudAcc = 0;
  private reducedMotion: boolean | null = null;
  private screenAcc = 0;
  private readonly offLocale: () => void;

  constructor(
    private readonly ctx: UiContext,
    mountPoint: HTMLElement,
  ) {
    this.root = mountPoint;
    this.layers = {
      hud: h('div', { class: 'layer layer-hud' }),
      screen: h('div', { class: 'layer layer-screen' }),
      modal: h('div', { class: 'layer layer-modal' }),
      toast: h('div', { class: 'layer layer-toast' }),
      overlay: h('div', { class: 'layer layer-overlay' }),
    };
    this.root.append(this.layers.hud, this.layers.screen, this.layers.modal, this.layers.toast, this.layers.overlay);
    this.offLocale = ctx.i18n.onChange(() => this.rebuildAll());
    uiAppRef.current = this;
  }

  /** Monte les composants permanents. Separe du constructeur pour que l'app choisisse le moment (apres sim.start()). */
  mount(): void {
    this.hud = new Hud(this.ctx).mount(this.layers.hud);
    this.toasts = new Toasts(this.ctx).mount(this.layers.toast);
    this.banner = new ResultBanner(this.ctx).mount(this.layers.overlay);
    this.tutorial = new TutorialOverlay(this.ctx).mount(this.layers.overlay);
  }

  openScreen(id: ScreenId): void {
    if (this.screen?.id === id) return;
    this.closeScreen();
    const comp = SCREENS[id](this.ctx).mount(this.layers.screen);
    this.screen = { id, comp };
    this.ctx.sim.dispatch({ type: 'ui.screenOpened', screen: id });
    this.ctx.sfx('button');
  }

  closeScreen(): void {
    this.screen?.comp.destroy();
    this.screen = null;
  }

  get currentScreen(): ScreenId | null {
    return this.screen?.id ?? null;
  }

  confirm(textKey: Parameters<UiContext['i18n']['t']>[0]): Promise<boolean> {
    return ConfirmDialog.ask(this.ctx, this.layers.modal, textKey);
  }

  /** Appele a chaque frame d'affichage par l'app (dt reel). */
  update(dt: number): void {
    this.hudAcc += dt;
    this.screenAcc += dt;
    if (this.hudAcc >= HUD_PERIOD) {
      this.syncMotionClass();
      this.hud?.update(this.hudAcc);
      this.banner?.update(this.hudAcc);
      this.tutorial?.update(this.hudAcc);
      this.toasts?.update(this.hudAcc);
      this.hudAcc = 0;
    }
    if (this.screenAcc >= SCREEN_PERIOD) {
      this.screen?.comp.update(this.screenAcc);
      this.syncOfflineModal();
      this.offline?.update(this.screenAcc);
      this.screenAcc = 0;
    }
  }

  /** Classe `reduce-motion` sur la racine (reglage du jeu ou preference systeme) : la CSS remplace les animations amples par des fondus. */
  private syncMotionClass(): void {
    const reduced = motionReduced(this.ctx);
    if (reduced === this.reducedMotion) return;
    this.reducedMotion = reduced;
    this.root.classList.toggle('reduce-motion', reduced);
  }

  private syncOfflineModal(): void {
    const pending = this.ctx.sim.query.offline();
    if (pending && !this.offline) this.offline = new OfflineModal(this.ctx).mount(this.layers.modal);
    if (!pending && this.offline) {
      this.offline.destroy();
      this.offline = null;
    }
  }

  /** Changement de langue : reconstruction complete (simple et sans risque d'oubli de texte). */
  private rebuildAll(): void {
    const reopen = this.screen?.id ?? null;
    this.dispose();
    this.mount();
    if (reopen) this.openScreen(reopen);
  }

  /** Destruction definitive (fin de session). */
  destroy(): void {
    this.dispose();
    this.offLocale();
    uiAppRef.current = null;
  }

  dispose(): void {
    this.closeScreen();
    for (const c of [this.hud, this.toasts, this.banner, this.tutorial, this.offline]) c?.destroy();
    this.hud = this.toasts = this.banner = this.tutorial = null;
    this.offline = null;
  }
}

/** Les composants qui doivent ouvrir un ecran recoivent l'UiApp via ce registre (evite les imports circulaires). */
export const uiAppRef: { current: UiApp | null } = { current: null };
